import React, { FC, useEffect, useRef, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import classNames from 'classnames';

import { ACCEPTED_DROPZONE_FILE_TYPES } from '@sv-support-sdk/utils/src/constants/attachments';
import Actions from '@sv-support-sdk/ui-components/src/components/ControlLayout/Actions';
import ActionsItem from '@sv-support-sdk/ui-components/src/components/ControlLayout/Actions/ActionsItem';
import Divider from '@sv-support-sdk/ui-components/src/components/Content/Divider';
import List from '@sv-support-sdk/ui-components/src/components/Lists/List';
import Overlay from '@sv-support-sdk/ui-components/src/components/Layout/Overlay';

import Textarea from '../../../components/Controls/Textarea';
import DocumentTag from '../../../components/Controls/DocumentTag';
import Button from '../../../components/Controls/Button';

import ticketsActions from '../../../store/actions/tickets';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  selectAttachmentsLoading,
  selectReplyAttachments,
  selectSelectedTicketLoading,
} from '../../../store/selectors/tickets';
import { $l } from '../../../locales';
import { showFileDropError } from '../../../store/actions/notifications';
import onDropHook from '../../../hooks/onDrop';
import useMobileDetect from '../../../hooks/useMobileDetect';

import css from '../style.module.scss';

interface IProps {
  ticketId: string;
  hash: string;
  isFormOpened: boolean;
  onOpenForm: () => void;
}

const MessageForm: FC<IProps> = ({ ticketId, hash, isFormOpened, onOpenForm }) => {
  const [message, setMessage] = useState('');
  const dispatch = useAppDispatch();
  const attachmentsLoading = useAppSelector(selectAttachmentsLoading);
  const replyAttachments = useAppSelector(selectReplyAttachments);
  const ticketLoading = useAppSelector(selectSelectedTicketLoading);
  const [onDrop] = onDropHook();
  const replyForm = useRef(null);
  const isMobile = useMobileDetect();

  useEffect(() => {
    if (isFormOpened) {
      //@ts-expect-error
      replyForm.current.scrollIntoView();
    }
  }, [isFormOpened]);

  const onDropRejected = (fileRejections: FileRejection[]) => {
    dispatch(showFileDropError(fileRejections));
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: ACCEPTED_DROPZONE_FILE_TYPES,
    onDropRejected,
    onDropAccepted: onDrop,
    noClick: true,
    maxSize: 10485760, // 10 MB
  });

  const changeMessage = ({ target: { value } }: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(value);
  };

  const onReplyClick = async () => {
    await dispatch(
      ticketsActions.replyToTicketAction(
        ticketId,
        { hash },
        { message, attachments: replyAttachments },
      ),
    );

    setMessage('');
    dispatch(ticketsActions.clearReplyAttachments());
  };

  if (!isFormOpened) {
    return (
      <div
        className={classNames(css.messageForm, {
          [css.isMobile]: isMobile,
        })}
      >
        <div className={css.messageForm_info}>{$l('TICKETS.OPEN_ON_REPLY_DESCRIPTION')}</div>
        <Button
          padding="0-26"
          onClick={() => onOpenForm()}
          width={isMobile ? undefined : 'auto'}
          text={$l('TICKETS.OPEN_ON_REPLY_BUTTON')}
        />
      </div>
    );
  }

  return (
    <>
      {isDragActive && <Overlay zIndex="1501" />}
      <div
        {...getRootProps()}
        className={classNames(css.messageForm, {
          [css.isInFocus]: isDragActive,
          [css.isMobile]: isMobile,
        })}
        ref={replyForm}
      >
        <Divider />
        <div className={css.messageForm_block}>
          <input {...getInputProps()} />
          <div className={css.messageForm_info}>{$l('TICKETS.ADDITIONAL_QUESTIONS')}</div>
          <Textarea
            className="indent-md-bottom"
            height="96"
            placeholder="Your comment"
            resizableByContent
            value={message}
            onChange={changeMessage}
          />
          {replyAttachments.length ? (
            <List className="indent-md-bottom" gap="16" direction="row" isWrapped>
              {replyAttachments.map(({ name, id, extension }) => (
                <DocumentTag
                  onClose={() => dispatch(ticketsActions.removeAttachmentFromMessage(id))}
                  text={name}
                  extension={extension}
                  key={id}
                />
              ))}
            </List>
          ) : null}
          <Actions
            direction={isMobile ? 'column_reverse' : undefined}
            gap={isMobile ? '32' : undefined}
            align="spaceBetween"
            width="full"
          >
            <ActionsItem width={isMobile ? 'full' : undefined}>
              <Button
                padding="0-64"
                onClick={() => dispatch(ticketsActions.markTicketAsResolved(ticketId, hash))}
                variant="primary-outlined"
                isDisabled={ticketLoading}
                text={$l('TICKETS.RESOLVE')}
              />
            </ActionsItem>
            <Actions width={isMobile ? 'full' : undefined}>
              <ActionsItem width={isMobile ? 'full' : undefined} className="indent-sm-right">
                <Button
                  variant="transparent"
                  isDisabled={attachmentsLoading}
                  onClick={open}
                  icon="20--attach-file"
                  iconSize="20"
                  padding={isMobile ? undefined : '0-26'}
                  text={$l('TICKETS.ATTACH_FILE')}
                />
              </ActionsItem>
              <ActionsItem width={isMobile ? 'full' : undefined}>
                <Button
                  padding={isMobile ? undefined : '0-64'}
                  isDisabled={ticketLoading || !message.trim()}
                  onClick={onReplyClick}
                  text={$l('TICKETS.REPLY')}
                />
              </ActionsItem>
            </Actions>
          </Actions>
        </div>
      </div>
    </>
  );
};

export default MessageForm;
